// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState, useEffect, useRef, type ChangeEvent } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import { api } from '@/lib/api'
import { useForm, Controller } from 'react-hook-form'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, FormActions, Input, Select, SearchableDropdown, Textarea } from '@/components/ui'
import {
  useMaintenanceRequest,
  useCreateMaintenanceRequest,
  useUpdateMaintenanceRequest,
} from '@/hooks/useMaintenanceRequests'
import { useProperties } from '@/hooks/useProperties'
import { usePropertyUnits, type PropertyUnit } from '@/hooks/useUnits'
import { useCustomers } from '@/hooks/useCustomers'
import type { Customer } from '@/types'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useFormTelemetry } from '@/hooks/useFormTelemetry'
import { useNotificationStore } from '@/stores/useNotificationStore'

interface MaintenanceFormData {
  title: string
  description: string
  category: string
  category_note: string
  priority: string
  customer_id: string
  status: string
}

const API_BASE = import.meta.env.VITE_API_URL || ''

// Presigned-URL image upload → bare UUID key (same mechanism as vehicle/parcel photos).
async function uploadMaintenanceImage(file: File): Promise<string> {
  const { data } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
    content_type: file.type,
  })
  await fetch(data.url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } })
  return data.key
}

const maintenanceImageSrc = (key: string): string =>
  key.startsWith('/api/') ? `${API_BASE}${key}` : key.startsWith('http') ? key : `${API_BASE}/api/images/${key}`

export function MaintenanceFormPage() {
  const { t } = useTranslation('maintenance')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const isEditing = !!id
  const requestId = Number(id)
  const navigate = useNavigate()
  const translateError = useApiErrorTranslator('maintenance')
  const addToast = useNotificationStore((s) => s.addToast)
  // EP-73 Tier-1: form_started:maintenance / form_submitted:maintenance lifecycle.
  const { markStarted, markSubmitted } = useFormTelemetry('maintenance')

  const { data: existingRequest, isLoading: isLoadingRequest } = useMaintenanceRequest(
    isEditing ? requestId : 0
  )
  const createRequest = useCreateMaintenanceRequest()
  const updateRequest = useUpdateMaintenanceRequest()

  const { data: propertiesData } = useProperties()
  const { data: accountSettings } = useAccountSettings()

  // null means "not yet edited by user" — derive from server data
  const [localPropertyId, setSelectedPropertyId] = useState<string | null>(null)
  const [localUnitId, setSelectedUnitId] = useState<string | null>(null)
  const [imageUrls, setImageUrls] = useState<string[]>([])
  const [uploadingPhotos, setUploadingPhotos] = useState(false)
  const [photoError, setPhotoError] = useState<string>()
  const photoInputRef = useRef<HTMLInputElement>(null)
  const selectedPropertyId = localPropertyId ?? (isEditing && existingRequest?.property?.id ? String(existingRequest.property.id) : '')
  const selectedUnitId = localUnitId ?? (isEditing && existingRequest?.unit_id ? String(existingRequest.unit_id) : '')

  // EP-116 T011 — both pickers below are CREATE-ONLY: in edit mode this page
  // renders the property/unit as disabled read-only <Input>s (below) and
  // drops the customer field entirely, so neither converted field is ever
  // shown with a pre-existing value to preserve. No saved-value / fallback-
  // fetch guard is needed here (unlike ParcelFormPage/LeaseFields, whose
  // pickers stay live in edit mode).

  // Unit picker: server search + page accumulation (EP-116 T011). Resets
  // whenever the search text OR the selected property changes — a unit list
  // from the previous building or a stale query must not survive either.
  const [unitSearch, setUnitSearch] = useState('')
  const [unitPage, setUnitPage] = useState(1)
  const [accumulatedUnits, setAccumulatedUnits] = useState<PropertyUnit[]>([])

  const { data: unitsData, isLoading: isLoadingUnits } = usePropertyUnits(
    selectedPropertyId ? Number(selectedPropertyId) : 0,
    unitPage,
    undefined,
    unitSearch || undefined,
  )

  useEffect(() => {
    setUnitPage(1)
    setAccumulatedUnits([])
  }, [unitSearch, selectedPropertyId])

  useEffect(() => {
    if (!unitsData?.data) return
    setAccumulatedUnits((prev) => {
      const next =
        unitPage === 1
          ? unitsData.data
          : [...prev, ...unitsData.data.filter((u) => !new Set(prev.map((p) => p.id)).has(u.id))]
      // Same contents => same reference, so React bails out of the
      // re-render — see OnboardingWizardPage's T010 accumulate effect for
      // why this matters (the effect must not depend on the query hook
      // handing back a referentially stable `data` for an unchanged result).
      const unchanged = next.length === prev.length && next.every((u, i) => u.id === prev[i]?.id)
      return unchanged ? prev : next
    })
  }, [unitsData, unitPage])

  // Customers cascade-filter by selected property (when one is chosen) so the
  // operator only sees tenants who actually live at the right property.
  // Previously this was useCustomers(1, 'active'), but "active" requires a
  // signed lease — wizard-created draft leases were silently excluded and the
  // dropdown showed empty. Drop the status filter; scope by property instead.
  // Server search + page accumulation added in EP-116 T011 (same reasoning
  // as the unit picker above); the property-scope arg is unchanged.
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerPage, setCustomerPage] = useState(1)
  const [accumulatedCustomers, setAccumulatedCustomers] = useState<Customer[]>([])

  const { data: customersData, isLoading: isLoadingCustomers } = useCustomers(
    customerPage,
    undefined,
    customerSearch || undefined,
    selectedPropertyId ? Number(selectedPropertyId) : undefined,
  )

  useEffect(() => {
    setCustomerPage(1)
    setAccumulatedCustomers([])
  }, [customerSearch, selectedPropertyId])

  useEffect(() => {
    if (!customersData?.data) return
    setAccumulatedCustomers((prev) => {
      const next =
        customerPage === 1
          ? customersData.data
          : [...prev, ...customersData.data.filter((c) => !new Set(prev.map((p) => p.id)).has(c.id))]
      const unchanged = next.length === prev.length && next.every((c, i) => c.id === prev[i]?.id)
      return unchanged ? prev : next
    })
  }, [customersData, customerPage])

  const properties = propertiesData?.data || []

  // Defensive: maintenance_categories entries are always {key,en,th} objects per
  // the current MaintenanceCategory type/strong params, but this guards any
  // legacy plain-string entry — branch on i18n.exists() since a custom category
  // has no fixed locale key. Extracted to a helper (typed on a plain `string`)
  // because inlining it makes TS narrow `cat` to `never` in the `typeof cat ===
  // 'string'` branch (the union type never actually includes a string member),
  // which then fails to type-check against the typed t() key overload.
  const formatCustomCategoryLabel = (key: string): string =>
    i18n.exists(`maintenance:categories.${key}`) ? t(`categories.${key}`) : key

  const configuredCategories = accountSettings?.settings?.maintenance_categories
  const defaultCategories = [
    { key: 'plumbing', en: 'Plumbing', th: 'ประปา' },
    { key: 'electrical', en: 'Electrical', th: 'ไฟฟ้า' },
    { key: 'appliance', en: 'Appliance', th: 'เครื่องใช้ไฟฟ้า' },
    { key: 'structural', en: 'Structural', th: 'โครงสร้าง' },
    { key: 'pest_control', en: 'Pest Control', th: 'กำจัดสัตว์รบกวน' },
    { key: 'cleaning', en: 'Cleaning', th: 'ทำความสะอาด' },
    { key: 'other', en: 'Other', th: 'อื่นๆ' },
  ]
  const categoryOptions = [
    { value: '', label: t('form.select_category') },
    ...(configuredCategories || defaultCategories).map(
      (cat) => ({
        value: typeof cat === 'string' ? cat : cat.key,
        label: typeof cat === 'string'
          ? formatCustomCategoryLabel(cat)
          : (i18n.language === 'th' ? cat.th : cat.en),
      })
    ),
  ]

  const priorityOptions = [
    { value: 'low', label: t('priority.low') },
    { value: 'normal', label: t('priority.normal') },
    { value: 'urgent', label: t('priority.urgent') },
  ]

  const propertyOptions = [
    { value: '', label: t('form.select_property') },
    ...properties.map((p) => ({ value: String(p.id), label: p.name })),
  ]

  // No leading blank/placeholder row — SearchableDropdown shows its
  // `placeholder` prop instead when unselected (same convention as
  // ParcelFormPage/AnnouncementFormPage's converted pickers). The options
  // list is exactly the server result: no client-side filter layered on it.
  const unitOptions = accumulatedUnits.map((u) => ({ value: String(u.id), label: u.name }))

  const customerOptions = accumulatedCustomers.map((c) => ({ value: String(c.id), label: c.name }))

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm<MaintenanceFormData>({
    defaultValues: {
      title: '',
      description: '',
      category: '',
      category_note: '',
      priority: 'normal',
      customer_id: '',
      status: 'open',
    },
  })

  const watchedCategory = watch('category')

  // Populate form when editing
  useEffect(() => {
    if (isEditing && existingRequest) {
      reset({
        title: existingRequest.title,
        description: existingRequest.description || '',
        category: existingRequest.category || '',
        category_note: existingRequest.category_note || '',
        priority: existingRequest.priority,
        customer_id: existingRequest.customer_id ? String(existingRequest.customer_id) : '',
        status: existingRequest.status || 'open',
      })
      setImageUrls(existingRequest.image_urls || [])
    }
  }, [isEditing, existingRequest, reset])

  const handlePhotoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    e.target.value = '' // reset so the same file can be re-picked after removal
    if (files.length === 0) return
    setPhotoError(undefined)
    setUploadingPhotos(true)
    try {
      const keys = await Promise.all(files.map(uploadMaintenanceImage))
      setImageUrls((prev) => [...prev, ...keys])
    } catch {
      setPhotoError(tc('file_upload.error'))
    } finally {
      setUploadingPhotos(false)
    }
  }

  const removePhoto = (key: string) => setImageUrls((prev) => prev.filter((k) => k !== key))

  const onSubmit = async (data: MaintenanceFormData) => {
    try {
      if (isEditing) {
        await updateRequest.mutateAsync({
          id: requestId,
          title: data.title,
          description: data.description || undefined,
          category: data.category || undefined,
          category_note: data.category === 'other' ? data.category_note || undefined : undefined,
          priority: data.priority,
          status: data.status,
          image_urls: imageUrls,
        })
        markSubmitted()
        navigate(`/maintenances/${requestId}`)
      } else {
        if (!selectedUnitId) return
        const result = await createRequest.mutateAsync({
          unit_id: Number(selectedUnitId),
          title: data.title,
          description: data.description || undefined,
          category: data.category || undefined,
          category_note: data.category === 'other' ? data.category_note || undefined : undefined,
          priority: data.priority,
          customer_id: data.customer_id ? Number(data.customer_id) : undefined,
          image_urls: imageUrls,
        })
        markSubmitted()
        navigate(`/maintenances/${result.id}`)
      }
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  if (isEditing && isLoadingRequest) {
    return (
        <div className="space-y-6">
          <Card>
            <div className="h-64 animate-pulse bg-base-200 rounded" />
          </Card>
        </div>
    )
  }

  return (
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: t('title'), href: '/maintenances' },
            { label: isEditing ? t('form.title_edit') : t('form.title_new') },
          ]}
        />

        <PageHeader
          title={isEditing ? t('form.title_edit') : t('form.title_new')}
          description={isEditing ? t('form.description_edit') : t('form.description_new')}
        />

        <Card>
          <form onSubmit={handleSubmit(onSubmit)} onChangeCapture={markStarted} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Unit Selection: Property first, then Unit */}
              {!isEditing && (
                <>
                  <Select
                    label={t('labels.property')}
                    value={selectedPropertyId}
                    onChange={(e) => {
                      setSelectedPropertyId(e.target.value)
                      setSelectedUnitId('')
                    }}
                    options={propertyOptions}
                  />
                  <SearchableDropdown
                    label={t('labels.unit')}
                    placeholder={t('form.select_unit')}
                    value={selectedUnitId}
                    onChange={setSelectedUnitId}
                    options={unitOptions}
                    disabled={!selectedPropertyId}
                    onSearch={setUnitSearch}
                    isLoading={isLoadingUnits}
                    hasMore={
                      !!unitsData?.meta &&
                      unitsData.meta.current_page < unitsData.meta.total_pages
                    }
                    onLoadMore={() => setUnitPage((p) => p + 1)}
                  />
                </>
              )}

              {isEditing && (
                <>
                  <Input
                    label={t('labels.property')}
                    value={existingRequest?.property?.name || '-'}
                    disabled
                    readOnly
                  />
                  <Input
                    label={t('labels.unit')}
                    value={existingRequest?.unit?.name || '-'}
                    disabled
                    readOnly
                  />
                </>
              )}

              <Input
                label={t('labels.title')}
                placeholder={t('form.title_placeholder')}
                error={errors.title?.message}
                {...register('title', { required: tc('errors.required') })}
              />

              <Select
                label={t('labels.category')}
                options={categoryOptions}
                {...register('category')}
              />

              {watchedCategory === 'other' && (
                <Input
                  label={t('form.category_note')}
                  placeholder={t('form.category_note_placeholder')}
                  {...register('category_note')}
                />
              )}

              <Select
                label={t('labels.priority')}
                options={priorityOptions}
                {...register('priority')}
              />

              {!isEditing && (
                <Controller
                  name="customer_id"
                  control={control}
                  render={({ field }) => (
                    <SearchableDropdown
                      label={t('labels.customer')}
                      placeholder={t('form.no_customer')}
                      options={customerOptions}
                      value={field.value}
                      onChange={field.onChange}
                      onSearch={setCustomerSearch}
                      isLoading={isLoadingCustomers}
                      hasMore={
                        !!customersData?.meta &&
                        customersData.meta.current_page < customersData.meta.total_pages
                      }
                      onLoadMore={() => setCustomerPage((p) => p + 1)}
                    />
                  )}
                />
              )}

              {isEditing && (
                <Select
                  label={t('labels.status')}
                  options={[
                    { value: 'open', label: t('status.open') },
                    { value: 'in_progress', label: t('status.in_progress') },
                    { value: 'completed', label: t('status.completed') },
                  ]}
                  {...register('status')}
                />
              )}
            </div>

            <Textarea
              label={t('labels.description')}
              placeholder={t('form.description_placeholder')}
              rows={4}
              {...register('description')}
            />

            {/* Photos — operators can attach reference images on create/edit (image_urls). */}
            <div className="space-y-2 min-w-0">
              <label className="label-text font-medium">{t('labels.photos')}</label>
              {imageUrls.length > 0 && (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                  {imageUrls.map((key) => (
                    <div
                      key={key}
                      className="relative aspect-square rounded-lg overflow-hidden bg-base-200 group"
                    >
                      <img
                        src={maintenanceImageSrc(key)}
                        alt={t('labels.photo')}
                        className="w-full h-full object-cover"
                      />
                      <Button
                        type="button"
                        shape="circle"
                        size="xs"
                        variant="error"
                        onClick={() => removePhoto(key)}
                        aria-label={tc('actions.remove')}
                        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  uploadingPhotos ? 'border-base-300 bg-base-200' : 'border-base-300 hover:border-primary'
                }`}
                onClick={() => photoInputRef.current?.click()}
              >
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoChange}
                  className="hidden"
                  disabled={uploadingPhotos}
                  data-testid="maintenance-photo-input"
                />
                <span className="text-sm text-base-content/60">
                  {uploadingPhotos ? tc('file_upload.uploading') : tc('file_upload.click_to_upload')}
                </span>
              </div>
              {photoError && (
                <p role="alert" className="text-error text-sm">
                  {photoError}
                </p>
              )}
            </div>

            <FormActions>
              <Button type="button" variant="ghost" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/maintenances'))}>
                {tc('actions.cancel')}
              </Button>
              <Button
                type="submit"
                loading={isEditing ? updateRequest.isPending : createRequest.isPending}
                disabled={!isEditing && !selectedUnitId}
              >
                {isEditing ? t('actions.update_request') : t('actions.create_request')}
              </Button>
            </FormActions>
          </form>
        </Card>
      </div>
  )
}
